import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../config/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import toast from 'react-hot-toast'

const { FiX, FiTrash2, FiAlertTriangle, FiDownload } = FiIcons

const DeleteAccountModal = ({ isOpen, onClose }) => {
  const { user, family, signOut } = useAuth()
  const [loading, setLoading] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [step, setStep] = useState(1) // 1: Warning, 2: Confirmation, 3: Final confirmation
  const [exportOffered, setExportOffered] = useState(false)

  const confirmationText = 'DELETE MY ACCOUNT'

  const handleExportData = async () => {
    // This would trigger the export functionality
    setExportOffered(true)
    toast.success('Data export will be sent to your email')
  }

  const handleDeleteAccount = async () => {
    if (confirmText !== confirmationText) {
      toast.error(`Please type "${confirmationText}" to confirm deletion.`)
      return
    }

    setLoading(true)
    try {
      console.log('Starting account deletion process...')

      // First, delete all family data using our database function
      if (family?.id) {
        const { data, error: dbError } = await supabase.rpc('delete_family_account', {
          family_uuid: family.id
        })

        if (dbError) {
          console.error('Database deletion error:', dbError)
          toast.error('Failed to delete family data. Please try again.')
          setLoading(false)
          return
        }
      }

      // Then delete the auth user
      const { error: authError } = await supabase.auth.admin.deleteUser(user.id)
      
      if (authError) {
        console.error('Auth deletion error:', authError)
        // Even if auth deletion fails, the user data is gone, so sign them out
      }

      toast.success('Account deleted successfully. We\'re sorry to see you go!')
      
      // Sign out the user
      await signOut()

    } catch (error) {
      console.error('Unexpected error during account deletion:', error)
      toast.error('An unexpected error occurred. Please contact support.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setStep(1)
    setConfirmText('')
    setExportOffered(false)
    onClose()
  }

  const renderStep1 = () => (
    <div>
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-2xl" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Delete Account</h2>
        <p className="text-gray-600">
          We're sorry to see you go! Before you delete your account, let's make sure you understand what will happen.
        </p>
      </div>

      <div className="space-y-4 mb-6">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="font-medium text-red-800 mb-2">What will be permanently deleted:</h3>
          <ul className="text-sm text-red-700 space-y-1">
            <li>• Your family profile and all children profiles</li>
            <li>• All tasks and chore history</li>
            <li>• All rewards and redemption history</li>
            <li>• Points and progress tracking</li>
            <li>• Analytics and usage data</li>
            <li>• Account settings and preferences</li>
          </ul>
        </div>

        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-medium text-blue-800 mb-2">Before you go:</h3>
          <div className="text-sm text-blue-700">
            Would you like to export your family's data for your records?
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportData}
            className="mt-2 border-blue-300 text-blue-700 hover:bg-blue-100"
            disabled={exportOffered}
          >
            <SafeIcon icon={FiDownload} className="text-xs" />
            {exportOffered ? 'Export Requested' : 'Export My Data'}
          </Button>
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={handleClose}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="danger"
          onClick={() => setStep(2)}
          className="flex-1"
        >
          Continue to Delete
        </Button>
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div>
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiTrash2} className="text-red-600 text-2xl" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Final Confirmation</h2>
        <p className="text-gray-600">
          This action cannot be undone. All your family's data will be permanently deleted.
        </p>
      </div>

      <div className="mb-6">
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiAlertTriangle} className="text-yellow-600 text-lg mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-yellow-800 mb-1">
                Are you absolutely sure?
              </h4>
              <p className="text-sm text-yellow-700">
                This will permanently delete the <strong>{family?.name || 'family'}</strong> account 
                and all associated data for <strong>{family?.children?.length || 0}</strong> children.
              </p>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            To confirm, type <strong>"{confirmationText}"</strong> below:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder={confirmationText}
          />
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setStep(1)}
          className="flex-1"
          disabled={loading}
        >
          Back
        </Button>
        <Button
          type="button"
          variant="danger"
          onClick={handleDeleteAccount}
          loading={loading}
          className="flex-1"
          disabled={confirmText !== confirmationText}
        >
          <SafeIcon icon={FiTrash2} className="text-sm" />
          {loading ? 'Deleting...' : 'Delete Account'}
        </Button>
      </div>
    </div>
  )

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>Step {step} of 2</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  className="p-2"
                  disabled={loading}
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </Button>
              </div>

              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                {step === 1 ? renderStep1() : renderStep2()}
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default DeleteAccountModal